import { useMemo } from 'react'
import { createContext, useContextSelector } from 'use-context-selector'
import defaultConfig, {
  type AppConfig,
  type AppConfigOverrides,
} from '@/app/config'

type ConfigContextValue = AppConfig

const ConfigContext = createContext<ConfigContextValue | undefined>(undefined)

const mergeConfig = (base: AppConfig, overrides: AppConfigOverrides = {}): AppConfig => ({
  ...base,
  ...overrides,
})

export function ConfigProvider({
  configOverrides,
  children,
}: {
  configOverrides?: AppConfigOverrides
  children: React.ReactNode
}) {
  // Nested providers layer their overrides on top of the parent's config.
  const parentConfig = useContextSelector(ConfigContext, (value) => value)
  const value = useMemo(
    () => mergeConfig(parentConfig ?? defaultConfig, configOverrides),
    [configOverrides, parentConfig],
  )
  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
}

const useConfigSelector = <T,>(selector: (value: ConfigContextValue) => T, name: string): T => {
  const selected = useContextSelector(ConfigContext, (value) => value ? selector(value) : undefined)
  if (selected === undefined) {
    throw new Error(`${name} must be used within a ConfigProvider`)
  }
  return selected
}

export const useConfig = () => useConfigSelector((value) => value, 'useConfig')
export const useConfigValue = <T,>(selector: (config: AppConfig) => T) =>
  useConfigSelector(selector, 'useConfigValue')

export type { AppConfig, AppConfigOverrides }
